import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

const GLYPHS = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789/<>_';
let glyphCursor = 0;

function getRandomInt(maxExclusive: number): number {
  if (maxExclusive <= 0) return 0;

  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const bytes = new Uint32Array(1);
    window.crypto.getRandomValues(bytes);
    return bytes[0] % maxExclusive;
  }

  // Fallback for environments without crypto support.
  glyphCursor = (glyphCursor + 1) % maxExclusive;
  return glyphCursor;
}

interface ScrambleTextProps {
  text: string;
  className?: string;
  /** ms per reveal tick */
  speed?: number;
}

/**
 * Decodes text with a terminal-style scramble the first time it enters view.
 */
export function ScrambleText({ text, className = '', speed = 28 }: ScrambleTextProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(text);
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    let frame = 0;
    const total = text.length;
    const id = setInterval(() => {
      frame++;
      const revealed = Math.floor(frame / 2);
      const next = text
        .split('')
        .map((ch, i) => {
          if (ch === ' ') return ' ';
          if (i < revealed) return ch;
          return GLYPHS[getRandomInt(GLYPHS.length)];
        })
        .join('');
      setDisplay(next);
      if (revealed >= total) {
        setDisplay(text);
        clearInterval(id);
      }
    }, speed);
    return () => clearInterval(id);
  }, [inView, text, speed]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
